'use client';

import { ChevronRight } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/shared/shadcn/components/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from '@/shared/shadcn/components/sidebar';

export const NavConfiguration = ({
  items,
}: {
  items: {
    title: string;
    url: string;
    isActive?: boolean;
    items?: {
      title: string;
      url: string;
    }[];
  }[];
}): JSX.Element => {
  const pathname = usePathname();
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});

  // Load open state from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('navConfigurationState');
    if (stored) {
      setOpenSections(JSON.parse(stored));
    }
  }, []);

  // Check if any child route matches current path and update state
  useEffect(() => {
    setOpenSections(prevOpenSections => {
      const newOpenSections = { ...prevOpenSections };
      items.forEach(item => {
        const isActive = item.items?.some(subItem => subItem.url === pathname);
        if (isActive) {
          newOpenSections[item.title] = true;
        }
      });
      localStorage.setItem('navConfigurationState', JSON.stringify(newOpenSections));
      return newOpenSections;
    });
  }, [pathname, items]);

  const handleToggle = (title: string, isOpen: boolean): void => {
    const newOpenSections = {
      ...openSections,
      [title]: isOpen,
    };
    setOpenSections(newOpenSections);
    localStorage.setItem('navConfigurationState', JSON.stringify(newOpenSections));
  };

  return (
    <SidebarGroup>
      <SidebarGroupLabel>
        <span className="truncate text-xs font-medium">Configuration</span>
      </SidebarGroupLabel>
      <SidebarMenu>
        {items.map(item => (
          <Collapsible
            key={item.title}
            asChild
            open={openSections[item.title]}
            onOpenChange={isOpen => handleToggle(item.title, isOpen)}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              {item.items?.length ? (
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton tooltip={item.title} className="font-normal">
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
              ) : (
                <SidebarMenuButton asChild tooltip={item.title} className="font-normal">
                  <Link href={item.url}>
                    <span>{item.title}</span>
                  </Link>
                </SidebarMenuButton>
              )}
              {item.items?.length ? (
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items?.map(subItem => (
                      <SidebarMenuSubItem key={subItem.title}>
                        <SidebarMenuSubButton asChild>
                          <Link href={subItem.url}>
                            <span>{subItem.title}</span>
                          </Link>
                        </SidebarMenuSubButton>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              ) : null}
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
};
